import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { CALCULATOR_LINEUP } from "@/lib/calculators/lineup";
import { getMetadata } from "@/lib/calculators/registry";

// ============================================================
// FYUNOR — Calculator Switcher
// The primary navigation. A horizontal, scrollable strip of every
// calculator in the lineup. Available calculators are clickable;
// upcoming ones are muted. The active calculator is highlighted.
// Reusable across every page via the Header.
// ============================================================

export default function CalculatorSwitcher() {
  const location = useLocation();

  const activeSlug = React.useMemo(() => {
    const m = location.pathname.match(/\/calculators\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.pathname]);

  return (
    <nav
      aria-label="Calculators"
      className="flex items-center gap-1 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {CALCULATOR_LINEUP.map((c) => {
        const meta = getMetadata(c.slug);
        const isActive = activeSlug === c.slug;
        const base =
          "inline-flex h-9 shrink-0 items-center rounded-lg px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

        if (!meta) {
          return (
            <span
              key={c.slug}
              className={cn(base, "text-subtle-foreground/50 cursor-default")}
              aria-disabled="true"
            >
              {c.name}
            </span>
          );
        }

        return (
          <Link
            key={c.slug}
            to={c.country ? `/${c.country.toLowerCase()}/calculators/${c.slug}` : `/calculators/${c.slug}`}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              base,
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
            )}
          >
            {c.name}
          </Link>
        );
      })}
    </nav>
  );
}